import React, { useEffect, useState } from 'react';
import {
    Table,
    Typography,
    Input,
    Switch,
    message,
    Skeleton,
    Form,
    Tooltip,
    Row,
    Col,
    Popconfirm,
    Segmented,
} from 'antd';
import FormButton from '../components/common/formbutton';
import { getRoles, createRole, toggleRoleStatus } from '../services/roleservice';
import { Role } from '../types/types';
import { useAuth } from '../auth/authcontext';

type StatusFilter = 'all' | 'active' | 'inactive';

const Roles: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
    const [searchText, setSearchText] = useState('');
    const [newRoleName, setNewRoleName] = useState('');
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [toggling, setToggling] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const { isReadOnly } = useAuth();

    useEffect(() => {
        fetchRoles();
    }, []);

    useEffect(() => {
        const term = searchText.toLowerCase();
        const filtered = roles.filter((r) => {
            const matchesQuery = r.roleName.toLowerCase().includes(term);

            const matchesStatus =
                statusFilter === 'all'
                    ? true
                    : statusFilter === 'active'
                        ? r.active
                        : !r.active;

            return matchesQuery && matchesStatus;
        });
        setFilteredRoles(filtered);
    }, [searchText, roles, statusFilter]);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const res = await getRoles();
            setRoles(res.data);
        } catch {
            message.error('Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRole = async () => {
        const trimmed = newRoleName.trim();

        if (!trimmed) {
            setError('Role name cannot be empty');
            return;
        }

        if (trimmed.length > 30) {
            setError('Role name must not exceed 30 characters');
            return;
        }

        if (trimmed.length < 3) {
            setError('Role name must be at least 3 characters');
            return;
        }

        const validName = /^[a-zA-Z0-9 _-]+$/.test(trimmed);
        if (!validName) {
            setError('Role name contains invalid characters');
            return;
        }

        const isDuplicate = roles.some(
            (r) => r.roleName.toLowerCase() === trimmed.toLowerCase()
        );
        if (isDuplicate) {
            setError('Role name already exists');
            return;
        }

        setCreating(true);
        try {
            await createRole(trimmed);
            message.success('Role created');
            setNewRoleName('');
            setError(null);
            fetchRoles();
        } catch {
            message.error('Failed to create role');
        } finally {
            setCreating(false);
        }
    };

    const handleToggleStatus = async (guid: string) => {
        setToggling(guid);
        try {
            await toggleRoleStatus(guid);
            message.success('Role status updated');
            fetchRoles();
        } catch {
            message.error('Failed to update role status');
        } finally {
            setToggling(null);
        }
    };

    return (
        <>
            <Typography.Title>Roles</Typography.Title>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={16} md={12} lg={10}>
                    <Form form={form}>
                        <Form.Item
                            validateStatus={error ? 'error' : ''}
                            help={error || ''}
                            style={{ marginBottom: 0 }}
                        >
                            <Tooltip
                                title="Role name must be at least 3 characters and can include letters, numbers, spaces, hyphens, and underscores."
                                placement="topLeft"
                            >
                                <Input
                                    maxLength={30}
                                    placeholder="New role name"
                                    value={newRoleName}
                                    allowClear
                                    onChange={(e) => {
                                        setNewRoleName(e.target.value);
                                        setError(null);
                                    }}
                                    style={{ maxWidth: '100%' }}
                                />
                            </Tooltip>
                        </Form.Item>
                    </Form>
                </Col>

                <Col xs={24} sm={8} md={6} lg={4}>
                    <FormButton
                        title="Create Role"
                        onClick={handleCreateRole}
                        loading={creating}
                        tooltip="Create new role"
                    />
                </Col>

                <Col xs={24} sm={24} md={12} lg={10}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <Input.Search
                            placeholder="Search roles"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            style={{ maxWidth: 300 }}
                        />

                        <Segmented
                            value={statusFilter}
                            onChange={(val) => setStatusFilter(val as StatusFilter)}
                            options={[
                                { label: 'All', value: 'all' },
                                { label: 'Active', value: 'active' },
                                { label: 'Inactive', value: 'inactive' },
                            ]}
                        />
                    </div>
                </Col>
            </Row>

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    dataSource={filteredRoles}
                    rowKey="guid"
                    pagination={{
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        defaultPageSize: 10,
                    }}
                >
                    <Table.Column
                        title="Role Name"
                        dataIndex="roleName"
                        sorter={(a: Role, b: Role) => a.roleName.localeCompare(b.roleName)}
                    />
                    <Table.Column
                        title="Active"
                        sorter={(a: Role, b: Role) => Number(a.active) - Number(b.active)}
                        render={(role: Role) => (
                            <Popconfirm
                                title={`Are you sure you want to ${role.active ? 'deactivate' : 'activate'
                                    } this role?`}
                                onConfirm={() => handleToggleStatus(role.guid)}
                                okText="Yes"
                                cancelText="No"
                                disabled={isReadOnly || role.roleName === 'Read'}
                            >
                                <Switch
                                    size="small"
                                    checked={role.active}
                                    loading={toggling === role.guid}
                                    disabled={isReadOnly || role.roleName === 'Read'}
                                />
                            </Popconfirm>
                        )}
                    />
                </Table>
            )}
        </>
    );
};

export default Roles;
